import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../lib/session';
import {
  Truck,
  Plus,
  Search,
  RotateCcw,
  Edit2,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Building2,
  Package,
  Layers,
  CreditCard,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  useSuppliers,
  useCreateSupplier,
  useMaterials,
  useMaterialCategories,
  useCreateMaterialCategory,
  useCreateMaterial,
  type NewSupplier,
  type SupplierRow,
} from '../lib/directory';
import { useLookups, getStandardMaterialUnitOptions } from '../lib/lookups';
import { useUpdateSupplier, useDeleteSupplier } from '../lib/directory-edit';
import { supabase } from '../lib/supabase';
import Shell from '../components/Shell';
import MultiSelect from '../components/MultiSelect';

import Confirm, { useConfirm } from '../components/Confirm';
import { useRestore } from '../lib/restore';
import { guardSupplier } from '../lib/delete-guards';
import RowActions from '../components/RowActions';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  initials,
  useToast,
  Select,
} from '../components/ui';

export default function Suppliers() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<SupplierRow | null>(null);
  const confirm = useConfirm<SupplierRow>();
  const restore = useRestore();
  const remove = useDeleteSupplier();
  const toast = useToast();
  const { data: materialsData } = useMaterials('');
  const { data, isLoading, error, refetch } = useSuppliers(search, materialType);

  async function doDelete() {
    if (!confirm.target) return;
    try {
      await remove.mutateAsync(confirm.target.id);
      const removed = confirm.target;
      toast.showWithAction(removed.name + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'suppliers', id: removed.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the supplier', true);
    } finally {
      confirm.close();
    }
  }

  // Filter options
  const hasFilters = Boolean(search || materialType);

  const materialOptions = [
    { value: '', label: 'All Materials' },
    ...(materialsData?.map((m) => ({ value: m.name, label: m.name })) ?? []),
  ];

  // Summary Metrics
  const totalSuppliers = data?.length ?? 0;
  const totalMaterialTypes = new Set(data?.flatMap((s) => s.materialTypes) ?? []).size;
  const uniqueCities = new Set(data?.map((s) => s.city).filter(Boolean)).size;

  return (
    <Shell
      title="Suppliers"
      crumb="Directory"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>Add Supplier</span>
        </button>
      }
    >
      {/* Quick Summary KPIs */}
      {!isLoading && data && (
        <motion.div
          className="kpi-row"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          style={{ marginBottom: 18 }}
        >
          <div className="kpi">
            <div className="lbl">
              <span>Total Suppliers</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Truck size={14} />
              </div>
            </div>
            <div className="val">{totalSuppliers}</div>
            <div className="sub">registered vendor network</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Material Categories</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Package size={14} />
              </div>
            </div>
            <div className="val" style={{ color: '#059669' }}>{totalMaterialTypes}</div>
            <div className="sub">active commodity trades</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Supply Hubs (Cities)</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f5f3ff', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MapPin size={14} />
              </div>
            </div>
            <div className="val">{uniqueCities}</div>
            <div className="sub">vendor delivery locations</div>
          </div>
        </motion.div>
      )}

      {/* Modern Filter Toolbar */}
      <div className="toolbar suppliers-toolbar">
        <div className="field-inline suppliers-search-box">
          <Search size={14} style={{ color: 'var(--text-faint)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search suppliers by name, phone, city, materials..."
            aria-label="Search suppliers"
          />
        </div>

        <div className="suppliers-filter-material">
          <Select
            value={materialType}
            onChange={(val) => setMaterialType(val)}
            placeholder="All Materials"
            options={materialOptions}
            searchable={materialOptions.length > 7}
            aria-label="Filter by material"
          />
        </div>

        {hasFilters && (
          <button
            className="btn btn-auto btn-sm btn-ghost suppliers-reset-btn"
            onClick={() => {
              setSearch('');
              setMaterialType('');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            aria-label="Reset all filters"
          >
            <RotateCcw size={13} />
            <span>Reset</span>
          </button>
        )}
      </div>

      {/* Suppliers Table Card */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="table-wrap">
          {isLoading ? (
            <TableSkeleton rows={5} cols={6} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.length ? (
            <EmptyState
              icon="truck"
              title={hasFilters ? 'No suppliers match your filters' : 'No suppliers yet'}
              hint={hasFilters ? 'Try clearing the filters above.' : 'Add your first supplier to record purchases against.'}
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', paddingLeft: 24 }}>Supplier Profile</th>
                  <th style={{ textAlign: 'left' }}>Materials Supplied</th>
                  <th style={{ textAlign: 'left' }}>Contact Phone</th>
                  <th style={{ textAlign: 'left' }}>Location</th>
                  <th style={{ textAlign: 'left' }}>GST Number</th>
                  <th style={{ textAlign: 'right', paddingRight: 24 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((s) => (
                  <tr
                    key={s.id}
                    className="clickable"
                    onClick={() => navigate(`/suppliers/${s.id}`)}
                  >
                    <td style={{ textAlign: 'left', paddingLeft: 24 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12 }}>
                        <div
                          className="avatar-sq"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 'var(--radius-xs)',
                            background: '#eff6ff',
                            color: '#2563eb',
                            fontWeight: 700,
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {initials(s.name)}
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <div className="name" style={{ fontWeight: 600, color: 'var(--text)' }}>
                            {s.name}
                          </div>
                          <div className="meta" style={{ color: 'var(--text-faint)', fontSize: 11.5 }}>
                            {s.contact_person || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'left', whiteSpace: 'normal', maxWidth: 320 }}>
                      {s.materialTypes.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-start' }}>
                          {s.materialTypes.map((t) => (
                            <span
                              key={t}
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-xs)',
                                fontSize: 11.5,
                                fontWeight: 500,
                                background: 'var(--surface-2)',
                                color: 'var(--text-soft)',
                                border: '1px solid var(--border-subtle)',
                              }}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-faint)' }}>—</span>
                      )}
                    </td>
                    <td className="num" style={{ textAlign: 'left', fontWeight: 500 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)' }}>
                        <Phone size={12} style={{ color: 'var(--text-faint)' }} />
                        <span>{s.phone}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)' }}>
                        <MapPin size={12} style={{ color: 'var(--text-faint)' }} />
                        <span>{s.city ?? '—'}</span>
                      </div>
                    </td>
                    <td className="num" style={{ textAlign: 'left', color: 'var(--text-soft)', fontSize: 12 }}>
                      {s.gst_number ? (
                        <span style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border-subtle)' }}>
                          {s.gst_number}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <RowActions
                      label={s.name}
                      onEdit={() => setEditing(s)}
                      onDelete={() => confirm.ask(s)}
                    />
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>

      {adding && <SupplierDrawer onClose={() => setAdding(false)} />}
      {editing && <SupplierDrawer existing={editing} onClose={() => setEditing(null)} />}

      {confirm.target && (
        <Confirm
          title={`Delete ${confirm.target.name}?`}
          message="They will disappear from your supplier list. You can undo this straight away from the message that appears."
          check={guardSupplier(confirm.target.id)}
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        />
      )}
    </Shell>
  );
}

const EMPTY: NewSupplier = {
  name: '', contact_person: '', phone: '', email: '',
  address: '', city: '', gst_number: '', remarks: '', materialIds: [], materialCategoryIds: [],
  bank_name: '', bank_holder: '', bank_account_no: '', bank_ifsc: '', bank_branch: '',
};

/** One drawer for both Add & Edit Supplier */
export function SupplierDrawer({ existing, onClose }: { existing?: SupplierRow; onClose: () => void }) {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);
  const { data: materialsData, refetch: refetchMaterials } = useMaterials('');
  const { data: categories } = useMaterialCategories();
  const createCategory = useCreateMaterialCategory();
  const createMaterial = useCreateMaterial();
  const { data: lk } = useLookups();
  const [showInlineAddMaterial, setShowInlineAddMaterial] = useState(false);
  const [newMatName, setNewMatName] = useState('');
  const [newMatBrand, setNewMatBrand] = useState('');
  const [newMatUnitId, setNewMatUnitId] = useState('');
  const [newMatError, setNewMatError] = useState('');
  const [isCustomUnit, setIsCustomUnit] = useState(false);
  const [customUnitName, setCustomUnitName] = useState('');
  const [form, setForm] = useState<NewSupplier>(
    existing
      ? {
          ...EMPTY,
          name: existing.name,
          contact_person: existing.contact_person ?? '',
          phone: existing.phone,
          city: existing.city ?? '',
          gst_number: existing.gst_number ?? '',
          materialIds: [],
          materialCategoryIds: [],
        }
      : EMPTY,
  );
  const [resolved, setResolved] = useState(!existing);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const toast = useToast();
  const busy = create.isPending || update.isPending || createCategory.isPending || createMaterial.isPending;

  useEffect(() => {
    if (!existing?.id) return;
    let cancel = false;
    supabase
      .from('suppliers')
      .select('email, address, city, gst_number, remarks, bank_name, bank_holder, bank_account_no, bank_ifsc, bank_branch')
      .eq('id', existing.id)
      .single()
      .then(({ data }) => {
        if (cancel || !data) return;
        setForm((f) => ({
          ...f,
          email: data.email ?? f.email,
          address: data.address ?? f.address,
          remarks: data.remarks ?? f.remarks,
          bank_name: data.bank_name ?? f.bank_name,
          bank_holder: data.bank_holder ?? f.bank_holder,
          bank_account_no: data.bank_account_no ?? f.bank_account_no,
          bank_ifsc: data.bank_ifsc ?? f.bank_ifsc,
          bank_branch: data.bank_branch ?? f.bank_branch,
        }));
      });
    return () => {
      cancel = true;
    };
  }, [existing?.id]);

  const unitOptions = useMemo(() => {
    return [
      ...getStandardMaterialUnitOptions(lk?.units),
      { value: '__other__', label: 'Other' },
    ];
  }, [lk?.units]);

  async function handleSaveInlineMaterial() {
    const trimmed = newMatName.trim();
    if (!trimmed) {
      setNewMatError('Material name is required');
      return;
    }

    try {
      let finalUnitId = newMatUnitId || unitOptions[0]?.value || '';

      if (isCustomUnit && customUnitName.trim()) {
        const trimmedCustom = customUnitName.trim();
        const found = lk?.units?.find(
          (u) =>
            u.name.toLowerCase() === trimmedCustom.toLowerCase() ||
            u.short_name.toLowerCase() === trimmedCustom.toLowerCase(),
        );
        if (found) {
          finalUnitId = found.id;
        } else if (actor) {
          const { data, error } = await supabase
            .from('units')
            .insert({
              company_id: actor.companyId,
              name: trimmedCustom,
              short_name: trimmedCustom.toLowerCase().slice(0, 10),
              sort_order: 99,
              is_active: true,
            })
            .select('id')
            .single();
          if (error) throw error;
          finalUnitId = data.id;
          void qc.invalidateQueries({ queryKey: ['lookups'] });
        }
      } else if (finalUnitId.startsWith('__std__')) {
        const stdName = finalUnitId.replace('__std__', '');
        const found = lk?.units?.find(
          (u) =>
            u.name.toLowerCase() === stdName.toLowerCase() ||
            u.short_name.toLowerCase() === stdName.toLowerCase(),
        );
        if (found) {
          finalUnitId = found.id;
        } else if (actor) {
          const { data, error } = await supabase
            .from('units')
            .insert({
              company_id: actor.companyId,
              name: stdName,
              short_name: stdName.toLowerCase().slice(0, 10),
              sort_order: 50,
              is_active: true,
            })
            .select('id')
            .single();
          if (error) throw error;
          finalUnitId = data.id;
          void qc.invalidateQueries({ queryKey: ['lookups'] });
        }
      }

      if (!finalUnitId || finalUnitId === '__other__') {
        finalUnitId = lk?.units?.[0]?.id || '';
      }

      const res = await createMaterial.mutateAsync({
        name: trimmed,
        brand: newMatBrand.trim() || undefined,
        unit_id: finalUnitId || undefined,
        rateRupees: '0',
        supplier_id: existing?.id || '',
        opening_qty: '0',
      });

      if (res?.id) {
        setForm((f) => ({
          ...f,
          materialIds: Array.from(new Set([...(f.materialIds ?? []), res.id])),
        }));
      }

      await refetchMaterials();
      toast.show(`${trimmed} added and selected`);
      setShowInlineAddMaterial(false);
      setNewMatName('');
      setNewMatBrand('');
      setNewMatUnitId('');
      setCustomUnitName('');
      setIsCustomUnit(false);
      setNewMatError('');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not add material', true);
    }
  }

  // Tick the materials this supplier already has, once we know their ids.
  if (existing && !resolved && materialsData?.length) {
    const ids = materialsData
      .filter((m) =>
        existing.materialTypes.includes(m.name) ||
        (m.categoryName && existing.materialTypes.includes(m.categoryName)),
      )
      .map((m) => m.id);
    setForm((f) => ({ ...f, materialIds: ids, materialCategoryIds: ids }));
    setResolved(true);
  }

  const set = (k: keyof NewSupplier) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Supplier name is required';
    if (!form.phone.trim()) next.phone = 'Phone number is required';
    else if (form.phone.replace(/\D/g, '').length < 10) next.phone = 'Enter a valid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email';
    setErrors(next);
    if (Object.keys(next).length) return;

    const selectedMatIds = form.materialIds ?? [];
    const catIdsToSave = new Set<string>();

    for (const matId of selectedMatIds) {
      const mat = materialsData?.find((m) => m.id === matId);
      if (mat?.categoryId) {
        catIdsToSave.add(mat.categoryId);
      } else if (mat) {
        const existingCat = categories?.find(
          (c) => c.name.toLowerCase() === mat.name.toLowerCase(),
        );
        if (existingCat) {
          catIdsToSave.add(existingCat.id);
        } else {
          try {
            const newCat = await createCategory.mutateAsync(mat.name);
            catIdsToSave.add(newCat.id);
          } catch {
            // fallback if already present or error
          }
        }
      } else {
        catIdsToSave.add(matId);
      }
    }

    const payload: NewSupplier = {
      ...form,
      materialCategoryIds: Array.from(catIdsToSave),
    };

    try {
      let supplierId = existing?.id;
      if (existing) {
        await update.mutateAsync({ ...payload, id: existing.id });
        toast.show(`${form.name} updated`);
      } else {
        const res = await create.mutateAsync(payload);
        supplierId = res.id;
        toast.show(`${form.name} added`);
      }

      if (supplierId && selectedMatIds.length > 0) {
        await supabase
          .from('materials')
          .update({ supplier_id: supplierId })
          .in('id', selectedMatIds);
      }

      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the supplier', true);
    }
  }

  return (
    <Drawer
      title={existing ? 'Edit Supplier' : 'Add Supplier'}
      description={existing ? existing.name : 'Register a new building material vendor'}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={existing ? 'Save Changes' : 'Create Supplier'}
      busy={busy}
    >
      <div className="form-grid">
        <Field label="Supplier Name / Firm" error={errors.name} span2>
          <input
            value={form.name}
            onChange={set('name')}
            autoFocus
            aria-invalid={!!errors.name}
          />
        </Field>

        <Field label="Contact Person" optional>
          <input
            value={form.contact_person}
            onChange={set('contact_person')}
          />
        </Field>

        <Field label="Primary Phone" error={errors.phone}>
          <input
            value={form.phone}
            onChange={set('phone')}
            inputMode="tel"
            aria-invalid={!!errors.phone}
          />
        </Field>

        <Field label="Email ID" error={errors.email} optional>
          <input
            type="email"
            value={form.email}
            onChange={set('email')}
            aria-invalid={!!errors.email}
          />
        </Field>

        <Field label="Location (City)">
          <input
            value={form.city}
            onChange={set('city')}
          />
        </Field>

        <Field label="GSTIN Number" optional span2>
          <input
            value={form.gst_number}
            onChange={set('gst_number')}
            style={{ textTransform: 'uppercase' }}
          />
        </Field>

        <Field label="Full Address / Warehouse Location" optional span2>
          <textarea
            value={form.address}
            onChange={set('address')}
          />
        </Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 12px' }}>
        <span
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '.08em',
            color: 'var(--accent)',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          Material Types Supplied
        </span>
        <button
          type="button"
          onClick={() => {
            setShowInlineAddMaterial((v) => !v);
            if (!newMatUnitId && lk?.units?.length) {
              setNewMatUnitId(lk.units[0]?.id ?? '');
            }
          }}
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            padding: '2px 4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--brand-primary, #2563eb)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.75')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          <Plus size={14} strokeWidth={2.4} />
          <span>Add Material</span>
        </button>
        <span style={{ height: 1, flex: 1, background: 'var(--border-subtle)' }} />
      </div>

      {showInlineAddMaterial && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '14px 16px',
            background: 'var(--surface-subtle, var(--surface))',
            boxShadow: 'var(--shadow-xs)',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
              paddingBottom: 8,
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              Add New Material
            </span>
            <button
              type="button"
              onClick={() => {
                setShowInlineAddMaterial(false);
                setNewMatName('');
                setNewMatBrand('');
                setNewMatError('');
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: 'var(--text-soft)',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="form-grid">
            <Field label="Material Name" error={newMatError} span2>
              <input
                value={newMatName}
                onChange={(e) => {
                  setNewMatName(e.target.value);
                  if (newMatError) setNewMatError('');
                }}
                autoFocus
              />
            </Field>

            <Field label="Brand / Spec" optional>
              <input
                value={newMatBrand}
                onChange={(e) => setNewMatBrand(e.target.value)}
              />
            </Field>

            <Field label={isCustomUnit ? 'Unit of Measurement (Custom)' : 'Unit of Measurement'}>
              {!isCustomUnit ? (
                <Select
                  value={newMatUnitId || unitOptions[0]?.value || ''}
                  onChange={(v) => {
                    if (v === '__other__') {
                      setIsCustomUnit(true);
                      setCustomUnitName('');
                    } else {
                      setNewMatUnitId(v);
                    }
                  }}
                  options={unitOptions}
                />
              ) : (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    value={customUnitName}
                    onChange={(e) => {
                      setCustomUnitName(e.target.value);
                      if (newMatError) setNewMatError('');
                    }}
                    placeholder="Type custom unit..."
                    autoFocus
                    style={{ paddingRight: 32 }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomUnit(false);
                      setCustomUnitName('');
                      setNewMatUnitId(unitOptions[0]?.value || '');
                    }}
                    style={{
                      position: 'absolute',
                      right: 8,
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-soft)',
                      cursor: 'pointer',
                      padding: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                    title="Back to dropdown"
                  >
                    <X size={15} />
                  </button>
                </div>
              )}
            </Field>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <button
              type="button"
              className="btn btn-sm btn-auto"
              onClick={() => {
                setShowInlineAddMaterial(false);
                setNewMatName('');
                setNewMatBrand('');
                setNewMatError('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm btn-auto"
              disabled={createMaterial.isPending}
              onClick={handleSaveInlineMaterial}
            >
              {createMaterial.isPending ? 'Saving…' : 'Add Material'}
            </button>
          </div>
        </div>
      )}

      <MultiSelect
        options={materialsData?.map((m) => ({ id: m.id, name: m.name }))}
        selected={form.materialIds ?? []}
        onChange={(ids) => setForm((f) => ({ ...f, materialIds: ids }))}
        emptyHint="No materials added in database yet. Click '+ Add Material' above to add one."
        collapseAfter={9}
        customLabel="Material"
      />

      <BankDetailsAccordion values={form} onChange={set} />

      <SectionTitle>Additional Notes</SectionTitle>
      <div className="form-grid">
        <Field label="Remarks & Terms" optional span2>
          <input
            value={form.remarks}
            onChange={set('remarks')}
          />
        </Field>
      </div>
    </Drawer>
  );
}

export function BankDetailsAccordion({
  values,
  onChange,
  defaultOpen,
}: {
  values: {
    bank_name?: string;
    bank_holder?: string;
    bank_account_no?: string;
    bank_ifsc?: string;
    bank_branch?: string;
  };
  onChange: (field: any) => (e: { target: { value: string } }) => void;
  defaultOpen?: boolean;
}) {
  const hasData = Boolean(
    values.bank_name?.trim() ||
    values.bank_holder?.trim() ||
    values.bank_account_no?.trim() ||
    values.bank_ifsc?.trim() ||
    values.bank_branch?.trim()
  );

  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  useEffect(() => {
    if (hasData) {
      setIsOpen(true);
    }
  }, [hasData]);

  return (
    <div
      style={{
        marginTop: 20,
        marginBottom: 16,
        borderRadius: 10,
        border: isOpen ? '1.5px solid #bfdbfe' : '1px solid var(--border)',
        background: isOpen ? '#ffffff' : 'var(--surface-subtle, #f8fafc)',
        boxShadow: isOpen ? '0 1px 3px rgba(0,0,0,0.04)' : 'none',
        overflow: 'hidden',
        transition: 'all 0.15s ease',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          width: '100%',
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          textAlign: 'left',
          gap: 12,
        }}
        aria-expanded={isOpen}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: hasData ? '#eff6ff' : '#f1f5f9',
              color: hasData ? '#2563eb' : '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={16} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                Bank Account Details
              </span>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: '1.5px 7px',
                  borderRadius: 999,
                  background: hasData ? '#dbeafe' : '#f1f5f9',
                  color: hasData ? '#1e40af' : '#64748b',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                {hasData ? 'Added' : 'Optional'}
              </span>
            </div>
            {!isOpen && (
              <div
                style={{
                  fontSize: 12,
                  color: hasData ? '#334155' : 'var(--text-soft)',
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {hasData
                  ? `${values.bank_name || 'Bank'} ${values.bank_account_no ? `• A/C: ${values.bank_account_no}` : ''} ${values.bank_ifsc ? `• IFSC: ${values.bank_ifsc}` : ''}`
                  : 'Click to open and enter bank account, IFSC & branch details'}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, color: '#2563eb' }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            {isOpen ? 'Close' : hasData ? 'Edit Details' : '+ Add Details'}
          </span>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {isOpen && (
        <div
          style={{
            padding: '12px 14px 14px',
            borderTop: '1px solid #e2e8f0',
            background: '#ffffff',
          }}
        >
          <div className="form-grid">
            <Field label="Bank Name" optional>
              <input
                value={values.bank_name || ''}
                onChange={onChange('bank_name')}
              />
            </Field>

            <Field label="Account Holder" optional>
              <input
                value={values.bank_holder || ''}
                onChange={onChange('bank_holder')}
              />
            </Field>

            <Field label="Account Number" optional>
              <input
                value={values.bank_account_no || ''}
                onChange={onChange('bank_account_no')}
                inputMode="numeric"
              />
            </Field>

            <Field label="IFSC Code" optional>
              <input
                value={values.bank_ifsc || ''}
                onChange={onChange('bank_ifsc')}
                style={{ textTransform: 'uppercase' }}
              />
            </Field>

            <Field label="Branch Name" optional span2>
              <input
                value={values.bank_branch || ''}
                onChange={onChange('bank_branch')}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

export function SectionTitle({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        color: 'var(--accent)',
        fontWeight: 700,
        margin: '22px 0 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        ...style,
      }}
    >
      <span style={{ whiteSpace: 'nowrap' }}>{children}</span>
      <span style={{ height: 1, flex: 1, background: 'var(--border-subtle)' }} />
    </div>
  );
}

